import { SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { black, colorArrayToRgb } from '@methanesat/colors';
import {
    Alert,
    Badge,
    BadgeProps,
    Box,
    Button,
    Carousel,
    CarouselSlide,
    ChipSelect,
    ChipSelectLabel,
    ColorRangeIcon,
    ColorRangeIconScale,
    ConcentrationsIcon,
    DispersedSourceIcon,
    FilterIcon,
    Link,
    MarkdownFormatter,
    PlumeIcon,
    styled,
    Typography,
    useTheme,
    ZoomInfoBox
} from '@methanesat/ui-components';

import {
    EMISSIONS_PRODUCT_UNITS,
    extendedRangeDispersedScaleLabels,
    FAQ_DISPERSED_SOURCES,
    getTargetTotalsColors,
    interpolateColorScalePercentages,
    METHANESAT_FAQ_URL
} from '../../../consts';
import { SHOW_L3_OPTION } from '../../../environmentVariables';
import { useL3UnitsLabel, useTranslate } from '../../../hooks';
import {
    selectAreaEmissionsWithinZoom,
    selectEmissionsMapInfraOperatorFilter,
    selectEmissionsMapInterface,
    selectEmissionsMapLayerConfigs,
    selectInfrastructureWithinZoom,
    selectMethaneProduct,
    selectPlatform,
    selectPlumeFluxWithinZoom,
    selectTargetWithinZoom,
    setL4MSATColorScale,
    toggleAreaEmissionChip,
    toggleOgiFilterChip,
    togglePlumeEmissionChip,
    toggleTargetEmissionChip
} from '../../../reducers';
import { AreaEmissionsProducts, MethaneLayerIds, Operator, Platforms } from '../../../types';
import {
    analytics,
    calculateDispersedIconColors,
    ErrorBoundary,
    getAreaEmissionsIconColors,
    getColorRangeColors,
    handleErrorBoundaryError
} from '../../../utils';
import { ColorThermometer } from '../../ColorThermometer';
import { InfrastructureOperatorFilter } from '../../MapControls';
import { AreaEmissionsProductToggle } from '../AreaEmissionsProductToggle';
import { AdvancedChipFunctions } from './AdvancedChipFunctions';
import { OgiChip } from './OgiChip';
import { TargetBoundaryLegend } from './TargetBoundaryLegend';

import type { RootState } from '../../../store';
import { RASTER_COLORMAP_NAMES } from '../../../types';
import ColorScalePicker from '../../ColorScalePicker';
import {
    generateColorStopLabels,
    getEmissionsThermometerColors,
    getL4MSATLabels,
    useAugmentLabels
} from './MiniLegendUtils';

interface MiniLegendProps {
    onOpenLegend: () => void;
    rightWidthOffset: number;
}

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: 2,
        top: 5,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px'
    }
}));

/**
 * The reduced version of the main legend. It allows the user to quickly understand the map
 * and to access some filtering features.
 */
const MiniLegend = ({ rightWidthOffset }: MiniLegendProps) => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const emissionsProduct = useSelector(selectMethaneProduct);

    // OGI Filter chip functions
    const isOgiOperatorFilterExpanded = useSelector(
        (state: RootState) => selectEmissionsMapInterface(state).ui.isOgiOperatorFilterExpanded
    );
    const handleFilterExpanded = (_event: SyntheticEvent, isExpanded: boolean) => {
        dispatch(toggleOgiFilterChip(isExpanded));
    };

    // Target emission expanded
    const isTargetEmissionChipExpanded = useSelector(
        (state: RootState) => selectEmissionsMapInterface(state).ui.isTargetEmissionsChipExpanded
    );
    const handleTargetEmissionChipExpanded = (_event: SyntheticEvent, isExpanded: boolean) => {
        dispatch(toggleTargetEmissionChip(isExpanded));
    };

    // Area emission expanded
    const isAreaEmissionsChipExpanded = useSelector(
        (state: RootState) => selectEmissionsMapInterface(state).ui.isAreaEmissionsChipExpanded
    );
    const handleAreaEmissionExpanded = (_event: SyntheticEvent, isExpanded: boolean) => {
        dispatch(toggleAreaEmissionChip(isExpanded));
    };

    // Plume chip expanded
    const isPlumeChipExpanded = useSelector(
        (state: RootState) => selectEmissionsMapInterface(state).ui.isPlumeEmissionsChipExpanded
    );
    const handlePlumeChipExpanded = (_event: SyntheticEvent, isExpanded: boolean) => {
        dispatch(togglePlumeEmissionChip(isExpanded));
    };

    const handleL4MSATColorScaleChange = (scale: RASTER_COLORMAP_NAMES) => {
        dispatch(setL4MSATColorScale(scale));
        analytics.changeL4ColorScale({ colorScale: scale, platform });
    };

    const areaEmissionLayerEnabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.areaEmissionRaster].enabled
    );
    const plumeEmissionLayerEnabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.plumeEmissionRate].enabled
    );

    const areaEmissionLayerWithinZoom = useSelector(selectAreaEmissionsWithinZoom);
    const plumeEmissionLayerWithinZoom = useSelector(selectPlumeFluxWithinZoom);
    const infrastructureLayerWithinZoom = useSelector(selectInfrastructureWithinZoom);
    const targetLayerWithinZoom = useSelector(selectTargetWithinZoom);
    const selectedOperators = useSelector(selectEmissionsMapInfraOperatorFilter);

    const product = useSelector(selectMethaneProduct);
    const platform = useSelector(selectPlatform);

    const colorRangeIconColors = getColorRangeColors(theme, areaEmissionLayerEnabled, product, platform);
    const targetTotalsColors = getTargetTotalsColors(theme, true);
    const emissionsThermometerColors = getEmissionsThermometerColors(platform, product, colorRangeIconColors);
    const l4MSATEmissionsColorScales = getL4MSATLabels(theme, areaEmissionLayerEnabled);

    const targetTooltip = `emissionsMapPage.miniLegend.${MethaneLayerIds.targets}.equivalencyTooltip.${platform}`;
    const l4Tooltip = `emissionsMapPage.miniLegend.${MethaneLayerIds.areaEmissionRaster}.equivalencyTooltip.${platform}`;

    const targetColorStopLabels = useAugmentLabels(
        generateColorStopLabels(platform, MethaneLayerIds.targets),
        EMISSIONS_PRODUCT_UNITS.target,
        targetTooltip
    );

    const l4EmissionsColorStopLabels = useAugmentLabels(
        platform === Platforms.MAIR
            ? generateColorStopLabels(platform, MethaneLayerIds.areaEmissionRaster)
            : extendedRangeDispersedScaleLabels,
        EMISSIONS_PRODUCT_UNITS.l4,
        l4Tooltip
    );

    const l3UnitsLabel = useL3UnitsLabel();

    // chip error boundary alert
    const ChipErrorBoundaryAlert = ({ resetErrorBoundary, id }: { id: string; resetErrorBoundary: () => void }) => {
        return (
            <>
                <Alert
                    sx={{
                        maxWidth: '100%',
                        fontSize: 10
                    }}
                    severity="error"
                >
                    {t('emissionsMapPage.error.general')}
                </Alert>
                <Button
                    color="error"
                    variant="text"
                    data-testid={`${id}-error-boundary-reset`}
                    onClick={resetErrorBoundary}
                >
                    {t('emissionsMapPage.error.retry')}
                </Button>
            </>
        );
    };

    // These are the elements to be rendered in different positions depending
    // on the screen size.  The elements will also appear and disappear at the
    // appropriate zoom levels
    const elements = [
        // regional emissions
        <CarouselSlide key="target-emissions">
            <ChipSelect
                data-testid="target-emissions"
                expanded={isTargetEmissionChipExpanded}
                expandIconId="target-emissions-expand"
                label={
                    <ChipSelectLabel
                        icon={<ColorRangeIcon colors={targetTotalsColors} scale={ColorRangeIconScale.linear} />}
                    >
                        {t('emissionsMapPage.miniLegend.targets.mairTarget')}
                    </ChipSelectLabel>
                }
                onChange={handleTargetEmissionChipExpanded}
                active={targetLayerWithinZoom}
            >
                {!targetLayerWithinZoom && (
                    <ZoomInfoBox data-testid="zoom-info-box">
                        <strong>{t('emissionsMapPage.miniLegend.zoomOut')}</strong>{' '}
                        {t('emissionsMapPage.miniLegend.toShowData')}
                    </ZoomInfoBox>
                )}
                <Typography variant="body2" sx={{ marginY: 1 }}>
                    <MarkdownFormatter markdown={t(`emissionsMapPage.miniLegend.targets.description.${platform}`)} />
                </Typography>

                <ColorThermometer
                    colors={interpolateColorScalePercentages(targetColorStopLabels, platform)}
                    labels={targetColorStopLabels}
                />

                <TargetBoundaryLegend
                    sx={(theme) => ({
                        padding: `${theme.spacing(2)} 0}`
                    })}
                />
            </ChipSelect>
        </CarouselSlide>,
        // area emissions
        <CarouselSlide key="area-emissions">
            <ChipSelect
                data-testid="area-emissions"
                expanded={isAreaEmissionsChipExpanded}
                expandIconId="area-emissions-expand"
                label={
                    <ChipSelectLabel
                        icon={
                            // Different icons depending on whether we are displaying L3 vs L4 data
                            product === AreaEmissionsProducts.l4 ? (
                                <DispersedSourceIcon
                                    colors={calculateDispersedIconColors(
                                        theme,
                                        areaEmissionLayerEnabled,
                                        product,
                                        platform
                                    )}
                                    sx={{ marginLeft: 1, marginTop: 1 }}
                                />
                            ) : (
                                <ConcentrationsIcon
                                    colors={getAreaEmissionsIconColors(
                                        theme,
                                        areaEmissionLayerEnabled,
                                        product,
                                        platform
                                    )}
                                    sx={{ marginLeft: 1, marginTop: 1 }}
                                />
                            )
                        }
                    >
                        {product === AreaEmissionsProducts.l3
                            ? t('emissionsMapPage.miniLegend.area-emission-raster.l3Title')
                            : t('emissionsMapPage.miniLegend.area-emission-raster.l4Title')}
                    </ChipSelectLabel>
                }
                onChange={handleAreaEmissionExpanded}
                active={areaEmissionLayerWithinZoom}
            >
                {!areaEmissionLayerWithinZoom && (
                    <ZoomInfoBox data-testid="zoom-info-box">
                        <strong>{t('emissionsMapPage.miniLegend.zoomIn')}</strong>{' '}
                        {t('emissionsMapPage.miniLegend.toShowData')}
                    </ZoomInfoBox>
                )}
                <Box sx={{ paddingBottom: 1 }}>
                    <Typography variant="body2">
                        {t(`emissionsMapPage.miniLegend.area-emission-raster.${emissionsProduct}Description`)}
                    </Typography>

                    {product === AreaEmissionsProducts.l4 && (
                        <Link
                            href={`${METHANESAT_FAQ_URL}${FAQ_DISPERSED_SOURCES}`}
                            underline="always"
                            variant="body2"
                            data-testid="emissions-faq-link"
                            target="_blank"
                        >
                            {t(`emissionsMapPage.miniLegend.area-emission-raster.l4DescriptionLink`)}
                        </Link>
                    )}
                </Box>

                {product === AreaEmissionsProducts.l4 && platform === Platforms.MAIR && (
                    <ColorThermometer
                        accentuateLowestValue={false}
                        colors={emissionsThermometerColors}
                        labels={l4EmissionsColorStopLabels}
                    />
                )}

                {product === AreaEmissionsProducts.l4 && platform === Platforms.MSAT && (
                    <ColorScalePicker
                        onColorScaleChange={handleL4MSATColorScaleChange}
                        colorScales={l4MSATEmissionsColorScales}
                        disabled={!(areaEmissionLayerEnabled && areaEmissionLayerWithinZoom)}
                    />
                )}

                {product === AreaEmissionsProducts.l3 && (
                    <ColorThermometer
                        accentuateLowestValue
                        colors={emissionsThermometerColors}
                        labels={[
                            {
                                percent: 100,
                                label: t('emissionsMapPage.miniLegend.generalEmissions.orMore', {
                                    value: `2280 ${l3UnitsLabel}`
                                })
                            },
                            { percent: 89, label: '2180' },
                            { percent: 74, label: '2080' },
                            { percent: 51, label: '1980' },
                            {
                                percent: 0,
                                label: t('emissionsMapPage.miniLegend.generalEmissions.orLess', { value: '1880' })
                            }
                        ]}
                    />
                )}

                {SHOW_L3_OPTION && (
                    <Box my={1}>
                        <AreaEmissionsProductToggle location="minilegend" />
                    </Box>
                )}

                <TargetBoundaryLegend
                    sx={(theme) => ({
                        padding: `${theme.spacing(2)} 0 ${theme.spacing(1)}`
                    })}
                />

                <AdvancedChipFunctions
                    label={
                        areaEmissionLayerEnabled
                            ? t('emissionsMapPage.miniLegend.hide')
                            : t('emissionsMapPage.miniLegend.show')
                    }
                    layerEnabled={areaEmissionLayerEnabled}
                    layerWithinZoom={areaEmissionLayerWithinZoom}
                    layerId={MethaneLayerIds.areaEmissionRaster}
                    analyticsEvent={(input) => {
                        // this needs to be called like this so check-analytics can see its there
                        analytics.toggleAreaEmissionData(input);
                    }}
                ></AdvancedChipFunctions>
            </ChipSelect>
        </CarouselSlide>,
        // plume emissions
        <CarouselSlide key="point-source-toggle-chip">
            <ChipSelect
                data-testid="point-source-toggle-chip"
                expanded={isPlumeChipExpanded}
                expandIconId="plume-emissions-expand"
                label={
                    <ChipSelectLabel
                        icon={
                            <PlumeIcon
                                sx={{
                                    marginTop: '5px'
                                }}
                                colors={getColorRangeColors(theme, plumeEmissionLayerEnabled)}
                            />
                        }
                    >
                        {t('emissionsMapPage.miniLegend.plume-emission-rate.label')}
                    </ChipSelectLabel>
                }
                onChange={handlePlumeChipExpanded}
                active={plumeEmissionLayerWithinZoom}
            >
                {!plumeEmissionLayerWithinZoom && (
                    <ZoomInfoBox data-testid="zoom-info-box">
                        <strong>{t('emissionsMapPage.miniLegend.zoomIn')}</strong>{' '}
                        {t('emissionsMapPage.miniLegend.toShowData')}
                    </ZoomInfoBox>
                )}
                {/* FormGroup aligns the elements vertically */}
                <Typography variant="body2">
                    <MarkdownFormatter markdown={t('emissionsMapPage.miniLegend.plume-emission-rate.description')} />
                </Typography>
                <AdvancedChipFunctions
                    label={
                        plumeEmissionLayerEnabled
                            ? t('emissionsMapPage.miniLegend.hide')
                            : t('emissionsMapPage.miniLegend.show')
                    }
                    layerEnabled={plumeEmissionLayerEnabled}
                    layerWithinZoom={plumeEmissionLayerWithinZoom}
                    layerId={MethaneLayerIds.plumeEmissionRate}
                    analyticsEvent={(input) => {
                        // this needs to be called like this so check-analytics can see its there
                        analytics.togglePlumeEmissionData(input);
                    }}
                />
            </ChipSelect>
        </CarouselSlide>,
        // ogi type filter chip
        <CarouselSlide key="ogi-type-chip">
            <OgiChip disabled={!infrastructureLayerWithinZoom} active={infrastructureLayerWithinZoom} />
        </CarouselSlide>,
        /** OGI operator filter Chip */
        <CarouselSlide key="filter-ogi-chip">
            <ChipSelect
                data-testid="filter-ogi-chip"
                expandIconId="ogi-operator-filter-expand"
                expanded={isOgiOperatorFilterExpanded}
                label={
                    <ChipSelectLabel
                        icon={
                            /** filter out manually added "null" for unknown which has an empty label */
                            <StyledBadge
                                badgeContent={selectedOperators.filter((operator: Operator) => operator.label).length}
                                color="primary"
                            >
                                <FilterIcon />
                            </StyledBadge>
                        }
                    >
                        {t('emissionsMapPage.miniLegend.ogiFilter.label')}
                    </ChipSelectLabel>
                }
                onChange={handleFilterExpanded}
                active={infrastructureLayerWithinZoom}
            >
                {!isOgiOperatorFilterExpanded ? null : (
                    <ErrorBoundary
                        onError={handleErrorBoundaryError('MiniLegend - OGI operator filter')}
                        fallbackRender={({ resetErrorBoundary }) => (
                            <ChipErrorBoundaryAlert id="ogi-operator-filter" resetErrorBoundary={resetErrorBoundary} />
                        )}
                    >
                        {!infrastructureLayerWithinZoom && (
                            <ZoomInfoBox data-testid="zoom-info-box">
                                <strong>{t('emissionsMapPage.miniLegend.zoomIn')}</strong>{' '}
                                {t('emissionsMapPage.miniLegend.toActivate')}
                            </ZoomInfoBox>
                        )}
                        <InfrastructureOperatorFilter
                            GridProps={{ paddingRight: 0, paddingBottom: 1 }}
                            isSmall={true}
                            rightWidthOffset={0}
                        />
                    </ErrorBoundary>
                )}
            </ChipSelect>
        </CarouselSlide>
    ].filter((element) => !!element);

    return (
        <Box
            sx={{
                width: 'fit-content',
                maxWidth: `calc(100% + ${rightWidthOffset}px)`
            }}
        >
            <Carousel>{elements}</Carousel>
        </Box>
    );
};

export default MiniLegend;
